"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { stegaClean } from "@sanity/client/stega";
import { CAP_TRIM } from "./QeKit";

const UNITS = ["DAYS", "HOURS", "MINUTES", "SECONDS"];
const pad = (n: number) => String(n).padStart(2, "0");

function remaining(target: number) {
  const left = Math.max(0, target - Date.now());
  const s = Math.floor(left / 1000);
  return [Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), s % 60];
}

/**
 * Figma 12375:33437 (hero desktop, 473.75 wide) / 12405:6603 contador (mobile, 361 wide = desktop × 0.762) /
 * 12375:10830 (final): glass wrapper → white card → label (uppercase, 70 %) → line bar (grey 40 % + green 35.5 %
 * segment) → four Bebas digits in #ededed boxes + unit labels. Digits hydrate after mount (no SSR mismatch).
 */
export default function QeCountdown({ label, labelMobile, note, noteClassName, target, className }: { label?: string; labelMobile?: string; note?: string; noteClassName?: string; target?: string; className?: string }) {
  const targetMs = target ? Date.parse(stegaClean(target)) : NaN;
  const [parts, setParts] = useState<number[] | null>(null);
  useEffect(() => {
    if (Number.isNaN(targetMs)) { setParts([0, 0, 0, 0]); return; }
    const tick = () => setParts(remaining(targetMs));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [targetMs]);
  const shown = parts ?? [0, 0, 0, 0];
  const mobile = labelMobile || label;

  return (
    <div role="timer" aria-label={label} className={cn("w-[45.125rem] max-w-full rounded-[0.875rem] border border-solid border-white/10 bg-white/10 p-[0.625rem] backdrop-blur-[3.29rem] lg:w-[59.2188rem] lg:rounded-[1.125rem] lg:p-[0.75rem] lg:backdrop-blur-[4.3188rem]", className)}>
      <div className="flex flex-col items-center gap-[1.5rem] rounded-[0.375rem] border border-solid border-white/30 bg-white px-[2.625rem] py-[2.125rem] text-black lg:gap-[2rem] lg:rounded-[0.5rem] lg:px-[3.5rem] lg:py-[2.75rem]">
        <div className="flex w-full flex-col gap-[1.375rem] lg:gap-[1.75rem]">
          <p className="mb-0 flex h-[2.0955rem] items-start justify-center text-center font-geist text-[1.75rem] leading-[normal] font-normal whitespace-nowrap uppercase opacity-70 lg:h-[2.75rem] lg:text-[2.25rem]">
            <span className="font-geist lg:hidden">{mobile}</span>
            <span className="hidden font-geist lg:inline">{label}</span>
          </p>
          <i aria-hidden="true" className="relative block h-[0.1905rem] w-full bg-[#989898]/40 lg:h-[0.25rem]"><i className="absolute inset-y-0 left-0 w-[35.5%] bg-[#39b158]" /></i>
        </div>
        <div className="flex w-full items-start justify-between">
          {shown.map((v, i) => (
            <div key={i} className="contents">
              {i > 0 && (
                <div className="flex w-[0.857rem] flex-col items-center pt-[1.5rem] lg:w-[1.125rem] lg:pt-[2rem]">
                  <p className={cn("mb-0 font-bebas text-[4rem] leading-[normal] text-black lg:text-[5.625rem]", CAP_TRIM)}>:</p>
                </div>
              )}
              <div className="flex flex-col items-center gap-[0.625rem] lg:gap-[0.75rem]">
                <div className={cn("flex items-center justify-center rounded-[0.375rem] bg-[#ededed] py-[1.125rem] lg:rounded-[0.5rem] lg:py-[1.5rem]", i === 0 ? "pr-[1.5rem] pl-[0.875rem] lg:pr-[2rem] lg:pl-[1.125rem]" : "px-[1.375rem] lg:px-[1.75rem]")}>
                  <p suppressHydrationWarning className={cn("mb-0 font-bebas text-[6rem] leading-[normal] whitespace-nowrap text-black tabular-nums lg:text-[8rem]", CAP_TRIM)}>{pad(v)}</p>
                </div>
                <p className="mb-0 text-center font-geist text-[1.75rem] leading-[2.5rem] font-normal whitespace-nowrap uppercase opacity-70 lg:text-[1.815rem] lg:leading-[normal]">{UNITS[i]}</p>
              </div>
            </div>
          ))}
        </div>
        {/* final mobile (12405:8927): event time under the digits, 8 px below them; same line box as the label */}
        {note && <p className={cn("mt-[-0.5rem] mb-0 h-[2.0955rem] w-full text-center font-geist text-[1.75rem] leading-[normal] font-normal whitespace-nowrap uppercase opacity-70 lg:hidden lg:h-[2.75rem]", noteClassName)}>{note}</p>}
      </div>
    </div>
  );
}
